import React from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import {AnswerElement} from '@src/models/voting';
import {ApiImage} from '@src/common/ui/ApiImage';
import {chip, interactiveCard} from '@src/common/ui/cardStyles';

function normalizePercent(value: number | null | undefined): number {
  if (value == null) {
    return 0;
  }
  return value <= 1 ? value * 100 : value;
}

function VotingInfoBox({
  data,
  currentVote,
  imageId,
}: {
  imageId: string;
  data: AnswerElement;
  currentVote: string;
}) {
  const selected = currentVote === data.answer.id;

  return (
    <Card $selected={selected} aria-pressed={selected}>
      <AvatarWrap>
        <ApiImage
          type="SMALL"
          width={56}
          height={56}
          imageId={imageId}
          alt={data.answer.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '14px',
          }}
        />
      </AvatarWrap>

      <Content>
        <Title>{data.answer.title}</Title>
        {data.answer.description ? (
          <Description>{data.answer.description}</Description>
        ) : null}
        {data.answer.city ? (
          <CityChip>
            <Image
              src="/icons/localization.svg"
              width={14}
              height={14}
              alt=""
            />
            {data.answer.city}
          </CityChip>
        ) : null}
      </Content>

      <SelectIndicator $selected={selected} aria-hidden="true">
        {selected ? <IndicatorDot /> : null}
      </SelectIndicator>
    </Card>
  );
}

const Card = styled.article<{$selected: boolean}>`
  ${interactiveCard};
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  border-color: ${(p) =>
    p.$selected ? p.theme.colors.violet600 : p.theme.colors.border};
  background: ${(p) =>
    p.$selected ? p.theme.colors.secondary100 : p.theme.colors.surface};
  box-shadow: ${(p) =>
    p.$selected ? p.theme.elevation.accent : p.theme.elevation.sm};
`;

const AvatarWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${(p) => p.theme.colors.primary100};
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.primary500};
  word-break: break-word;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.textMuted};
  word-break: break-word;
`;

const CityChip = styled.span`
  ${chip};
  align-self: flex-start;
  margin-top: 2px;
`;

const SelectIndicator = styled.div<{$selected: boolean}>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid
    ${(p) =>
      p.$selected ? p.theme.colors.violet600 : p.theme.colors.primary300};
  display: grid;
  place-items: center;
  background: ${(p) =>
    p.$selected ? p.theme.colors.violet600 : p.theme.colors.neutral100};
  transition: border-color ${(p) => p.theme.motion.duration.base} ease,
    background ${(p) => p.theme.motion.duration.base} ease;
`;

const IndicatorDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.neutral100};
`;

export default VotingInfoBox;
export {normalizePercent};
